import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import moment from 'moment';
import { Toaster } from 'react-hot-toast';
import RemoteBlackboard from './RemoteBlackboard';
import ZoomButton from './ZoomButton';
import { REMOTE_MEMBER_STATUS, REMOTE_MEMBER_TYPE } from '../../lib/constants';
import MembersDialog from './MembersDialog';
import ControleSwitchButton from './ControleSwitchButton';
import RemoteCanvasWrapper from './RemoteCanvasWrapper';
import ChatDialog from './ChatDialog';
import { setExpansionValue } from '../../lib/remote';
import { showErrorToast } from '../../lib/tools';
import ColorSF2 from '../../styles/common/ColorSF2';
import RemoteHeader from './RemoteHeader';

// divタグを継承してコンポーネントを作成
const MainBlock = styled.div`
  width: 100vw;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${ColorSF2.lightGray};
`;
const MemberDialogWrapper = styled.div`
  position: absolute;
  right: 10px;
  top: -8px;
  z-index: 1;
`;
const ContentsWrapper = styled.div`
  height: calc(100% - 48px);
  display: flex;
  position: relative;
`;
const SideBar = styled.div`
  width: 64px;
  background-color: ${ColorSF2.paleBlue};
`;
const SideBarFunctionWrapper = styled.div`
  position: relative;
`;
const ZoomButtonWrapper = styled.div`
  position: absolute;
  width: 200px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${ColorSF2.skyBlue80};
  padding: 10px;
  right: 64px;
  top: 0px;
`;
const ChatDialogWrapper = styled(ZoomButtonWrapper)`
  width: 360px;
  background-color: ${ColorSF2.skyBlue80};
`;
const RemoteBlackboardWrapper = styled.div`
  position: absolute;
  right: 64px;
`;
/**
 * 遠隔支援画面を表示するコンポーネント
 *
 * @returns {ReactElement}
 */
const RemotePage = ({
  locale,
  userInfo,
  roomMembers,
  roomEntranceInfo,
  toggleSticker,
  togglePaint,
  toggleRemoteControl,
  toggleSound,
  toggleVideo,
  toggleBlackboard,
  toggleLight,
  togglePause,
  isVideo,
  isSticker,
  isPaint,
  isSound,
  isRemoteControl,
  isBlackboard,
  isLight,
  isPause,
  sendTextInfo,
  onUpdateText,
  onSendShow,
  onSendColor,
  onSendOpacity,
  onSendPosition,
  onCreateBlackboard,
  onSendBlackboardText,
  onSendPhoto,
  expand,
  contract,
  resetZoom,
  blackboardInfo,
  shareVideo,
  callAgain,
  normalSupport,
  incomingParticipantIds,
  outgoingParticipantIds,
  onSendChatData,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const [chatHistories, setChatHistories] = useState([]);
  const [openMembersDialog, setOpenMembersDialog] = useState(false);
  const [openZoomDialog, setOpenZoomDialog] = useState(false);
  const [openChatDialog, setOpenChatDialog] = useState(false);
  const expansionValue = 1;
  const { roomCreatedAt = undefined, appRoomId } = roomEntranceInfo;
  const currentSharer =
    roomMembers
      .filter(member => member.memberType === REMOTE_MEMBER_TYPE.client)
      .shift() || {};
  const sharerJoined =
    Object.keys(currentSharer).length > 0 &&
    (!normalSupport || (normalSupport && incomingParticipantIds.length));
  const { name: currentSharerName = '', device = {} } = currentSharer;
  const deviceName = (device && device.deviceDisplayName) || '';
  const callRemoteContoleFunction = (call, func) => {
    if (call) {
      func();
    }
  };
  const expirationDate =
    (roomCreatedAt &&
      formatMessage({ id: 'remotePage.supportEnd.title' }) +
        moment(roomCreatedAt).add(4, 'h').format('MM/DD HH:mm')) ||
    '';
  const memberCount = roomMembers.length;
  const clickContoller = (type, func) => {
    if (type !== 'chat') {
      setOpenChatDialog(false);
    }
    if (type !== 'zoom') {
      setOpenZoomDialog(false);
    }
    if (type !== 'board' && isBlackboard) {
      toggleBlackboard();
    }
    func();
  };
  // 着信拒否の通知処理
  const [denyToastedIds, setDenyToastedIds] = useState([]);
  useEffect(() => {
    // 繰り返し取得する情報の為、一度toastした人の情報は表示しない
    const deniedMembers = roomMembers.filter(
      member =>
        member.status === REMOTE_MEMBER_STATUS.deny &&
        !denyToastedIds.includes(member.operatorId),
    );
    if (deniedMembers.length > 0) {
      const work = denyToastedIds.concat(
        deniedMembers.map(member => member.operatorId),
      );
      setDenyToastedIds(work); // 拒否後に再呼出しなしの為追加のみ

      showErrorToast(
        formatMessage(
          { id: 'remotePage.message.denied' },
          { name: deniedMembers.map(member => member.name).join('、') },
        ),
      );
    }
  }, [roomMembers, denyToastedIds, setDenyToastedIds, formatMessage]);
  // 再通知ボタン押下処理
  const onClickCallAgain = param => {
    callAgain(param);
    // TODO 拒否メンバーへの再通知が可能になった場合は以下の行を有効にする
    // const { operatorId } = param;
    // if (denyToastedIds.includes(operatorId)) {
    //   setDenyToastedIds(denyToastedIds.filter(toastedId => toastedId !== operatorId));
    // }
  };
  return (
    <MainBlock
      className="RemotePage"
      onClick={() => {
        setOpenZoomDialog(false);
        setOpenChatDialog(false);
      }}
    >
      <Toaster />
      <RemoteHeader
        currentSharerName={currentSharerName}
        deviceName={deviceName}
        roomCreatedAt={roomCreatedAt}
        sharerJoined={sharerJoined}
        expirationDate={expirationDate}
        expirationValue={expansionValue}
        setExpansionValue={setExpansionValue}
        roomMembers={roomMembers}
        memberCount={memberCount}
        openMembersDialog={openMembersDialog}
        setOpenMembersDialog={setOpenMembersDialog}
      />
      <ContentsWrapper>
        <RemoteCanvasWrapper isWaiting={!sharerJoined} />
        <SideBar onClick={e => e.stopPropagation()}>
          <ControleSwitchButton
            id="toggleTransModeCap"
            type="video"
            label="remotePage.button.label.video"
            enabled={sharerJoined}
            active={isVideo}
            onClick={() => {
              clickContoller('video', () => {
                setOpenZoomDialog(false);
                callRemoteContoleFunction(sharerJoined, toggleVideo);
              });
            }}
          />
          <ControleSwitchButton
            id="toggleRemoteControlButton"
            type="remote"
            label="remotePage.button.label.remote"
            enabled={sharerJoined && !isVideo}
            active={isRemoteControl}
            onClick={() =>
              callRemoteContoleFunction(sharerJoined, toggleRemoteControl)
            }
          />
          <ControleSwitchButton
            id="toggleSoundButton"
            type="mic"
            label="remotePage.button.label.mic"
            active={isSound}
            onClick={() => toggleSound()}
          />
          <ControleSwitchButton
            id="togglePauseButton"
            type="pause"
            label="remotePage.button.label.pause"
            enabled={sharerJoined}
            active={isPause}
            onClick={() => togglePause()}
          />
          <ControleSwitchButton
            id="togglePaintButton"
            type="paint"
            label="remotePage.button.label.pen"
            enabled={sharerJoined}
            active={isPaint}
            onClick={() => callRemoteContoleFunction(sharerJoined, togglePaint)}
          />
          <ControleSwitchButton
            id="toggleStickerButton"
            type="sticker"
            label="remotePage.button.label.pointer"
            enabled={sharerJoined}
            active={isSticker}
            onClick={() =>
              callRemoteContoleFunction(sharerJoined, toggleSticker)
            }
          />
          <ControleSwitchButton
            id="toggleChat"
            type="chat"
            label="remotePage.button.label.chat"
            enabled={sharerJoined}
            active={openChatDialog}
            onClick={() =>
              clickContoller('chat', () => setOpenChatDialog(!openChatDialog))
            }
          />
          <ControleSwitchButton
            id="toggleLight"
            type="light"
            label="remotePage.button.label.light"
            enabled={sharerJoined}
            active={isLight}
            onClick={() => callRemoteContoleFunction(sharerJoined, toggleLight)}
          />
          <ControleSwitchButton
            id="toggleSendTextButton"
            type="board"
            label="remotePage.button.label.sendText"
            enabled
            active={isBlackboard}
            onClick={() =>
              clickContoller('board', () =>
                callRemoteContoleFunction(true, toggleBlackboard),
              )
            }
          />
          <SideBarFunctionWrapper>
            <ControleSwitchButton
              id="toggleZoomButton"
              type="zoom"
              label="remotePage.button.label.zoom"
              enabled={sharerJoined}
              active={openZoomDialog}
              onClick={() =>
                clickContoller('zoom', () => setOpenZoomDialog(!openZoomDialog))
              }
            />
            {openZoomDialog && (
              <ZoomButtonWrapper onClick={e => e.stopPropagation()}>
                <ZoomButton
                  action={() => callRemoteContoleFunction(sharerJoined, expand)}
                  type="expand"
                />
                <ZoomButton
                  action={() =>
                    callRemoteContoleFunction(sharerJoined, contract)
                  }
                  type="contract"
                />
                <ZoomButton
                  action={() =>
                    callRemoteContoleFunction(sharerJoined, resetZoom)
                  }
                  type="reset"
                />
              </ZoomButtonWrapper>
            )}
          </SideBarFunctionWrapper>
          <ControleSwitchButton
            id="togglePhotoButton"
            type="photo"
            label="remotePage.button.label.photo"
            enabled={sharerJoined}
            onClick={() => onSendPhoto()}
          />
        </SideBar>
        {openMembersDialog && (
          <MemberDialogWrapper>
            <MembersDialog
              userInfo={userInfo}
              appRoomId={appRoomId}
              members={roomMembers}
              currentSharerParticipantId={currentSharer.participantId || ''}
              incomingParticipantIds={incomingParticipantIds}
              outgoingParticipantIds={outgoingParticipantIds}
              shareVideo={shareVideo}
              callAgain={onClickCallAgain}
              close={() => setOpenMembersDialog(false)}
            />
          </MemberDialogWrapper>
        )}
        {isBlackboard && (
          <RemoteBlackboardWrapper>
            <RemoteBlackboard
              locale={locale}
              blackboardInfo={blackboardInfo}
              sendTextInfo={sendTextInfo}
              onClose={toggleBlackboard}
              onSendShow={onSendShow}
              onSendColor={onSendColor}
              onSendOpacity={onSendOpacity}
              onSendPosition={onSendPosition}
              onUpdateText={onUpdateText}
              onCreateBlackboard={onCreateBlackboard}
              onSendBlackboardText={onSendBlackboardText}
              sendButtonDisabled={!sharerJoined}
            />
          </RemoteBlackboardWrapper>
        )}
        {openChatDialog && (
          <ChatDialogWrapper onClick={e => e.stopPropagation()}>
            <ChatDialog
              userInfo={userInfo}
              members={roomMembers}
              sharerJoined={sharerJoined}
              chatHistories={chatHistories}
              setChatHistories={setChatHistories}
              send={chatData => {
                callRemoteContoleFunction(sharerJoined, () =>
                  onSendChatData(chatData),
                );
              }}
              close={() => setOpenChatDialog(false)}
            />
          </ChatDialogWrapper>
        )}
      </ContentsWrapper>
    </MainBlock>
  );
};

RemotePage.propTypes = {
  locale: PropTypes.string.isRequired,
  userInfo: PropTypes.object.isRequired,
  roomMembers: PropTypes.array.isRequired,
  roomEntranceInfo: PropTypes.object.isRequired,
  toggleRemoteControl: PropTypes.func.isRequired,
  toggleSticker: PropTypes.func.isRequired,
  togglePaint: PropTypes.func.isRequired,
  toggleSound: PropTypes.func.isRequired,
  toggleVideo: PropTypes.func.isRequired,
  toggleBlackboard: PropTypes.func.isRequired,
  toggleLight: PropTypes.func.isRequired,
  togglePause: PropTypes.func.isRequired,
  isVideo: PropTypes.bool.isRequired,
  // isWaiting: PropTypes.bool.isRequired,
  isSticker: PropTypes.bool.isRequired,
  isPaint: PropTypes.bool.isRequired,
  isSound: PropTypes.bool.isRequired,
  isRemoteControl: PropTypes.bool.isRequired,
  isBlackboard: PropTypes.bool.isRequired,
  isLight: PropTypes.bool.isRequired,
  isPause: PropTypes.bool.isRequired,
  sendTextInfo: PropTypes.object.isRequired,
  onUpdateText: PropTypes.func.isRequired,
  onSendShow: PropTypes.func.isRequired,
  onSendColor: PropTypes.func.isRequired,
  onSendOpacity: PropTypes.func.isRequired,
  onSendPosition: PropTypes.func.isRequired,
  onCreateBlackboard: PropTypes.func.isRequired,
  onSendBlackboardText: PropTypes.func.isRequired,
  onSendPhoto: PropTypes.func.isRequired,
  expand: PropTypes.func.isRequired,
  contract: PropTypes.func.isRequired,
  resetZoom: PropTypes.func.isRequired,
  blackboardInfo: PropTypes.object.isRequired,
  shareVideo: PropTypes.func.isRequired,
  callAgain: PropTypes.func.isRequired,
  normalSupport: PropTypes.bool.isRequired,
  incomingParticipantIds: PropTypes.array.isRequired,
  outgoingParticipantIds: PropTypes.array.isRequired,
  onSendChatData: PropTypes.func.isRequired,
};

export default RemotePage;
